import React, { useMemo } from "react";
import { Box, Typography, Grid, Card, CardContent } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const STACK_ID = "a";

interface InfoCardProps {
  title: string;
  value: string | number;
}

type DifficultyStats = {
  correct_mc_count: number;
  incorrect_mc_count: number;
};

const InfoCard: React.FC<InfoCardProps> = ({ title, value }) => (
  <Card
    sx={{ textAlign: "center", bgcolor: "#fff", display: "flex", justifyContent: "center", boxShadow: 2, borderRadius: 2, height: "100%" }}
  >
    <CardContent>
      <Typography variant="h3">
        <b>{value}</b>
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {title}
      </Typography>
    </CardContent>
  </Card>
);

interface DifficultyBarChartProps {
  data: { difficulty: string; correct_mc_count: number; incorrect_mc_count: number }[];
  title: string;
  padding: number;
}

const DifficultyBarChart: React.FC<DifficultyBarChartProps> = ({ data, title, padding }) => (
  <Grid
    item
    xs={12}
    md={12}
    lg={12}
    sx={{
      paddingTop: "8px !important",
      paddingLeft: `${padding}px !important`,
      display: "flex",
    }}
  >
    <Card
      sx={{
        p: 0,
        boxShadow: 3,
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Typography
        variant="h6"
        sx={{ textAlign: "center", fontWeight: "bold", margin: "1px", mt: "8px" }}
      >
        {title}
      </Typography>

      <CardContent sx={{ flexGrow: 1, p: 0 }}>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} margin={{ left: 16, right: 16 }} barGap={5}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="difficulty" stroke="#555" style={{ fontSize: "12px" }} />
            <YAxis stroke="#555" />
            <Tooltip />
            <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: "13px", paddingTop: "10px" }} />

            {/* Same palette + stacking behavior as your topic chart */}
            <Bar
              dataKey="correct_mc_count"
              name="Correct"
              stackId={STACK_ID}
              fill="#388E3C"
              barSize={20}
            />
            <Bar
              dataKey="incorrect_mc_count"
              name="Incorrect"
              stackId={STACK_ID}
              fill="#D32F2F"
              barSize={20}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  </Grid>
);

const MalwareAnalysisModal = ({ malwareSummary }: any) => {
  const series = useMemo(
    () => [
      { key: "correct_mc_count", label: "Correct", fill: "#388E3C" },
      { key: "incorrect_mc_count", label: "Incorrect", fill: "#D32F2F" },
    ],
    []
  );

  // Topic-wise distribution
  const topicData = useMemo(() => {
    const perTopic = malwareSummary?.stat_per_model_per_topic;
    if (!perTopic) return [];

    return Object.entries(perTopic).map(([topicName, stats]: any) => ({
      topic: topicName,
      correct_mc_count: stats?.correct_mc_count ?? 0,
      incorrect_mc_count: stats?.incorrect_mc_count ?? 0,
    }));
  }, [malwareSummary]);

  // ✅ Difficulty-wise distribution (easy/medium/hard)
  const difficultyData = useMemo(() => {
    const perDiff = malwareSummary?.stat_per_model_per_difficulty as
      | Record<string, DifficultyStats>
      | undefined;

    if (!perDiff) return [];

    const order = ["easy", "medium", "hard"];
    const labelMap: Record<string, string> = {
      easy: "Easy",
      medium: "Medium",
      hard: "Hard",
    };

    return Object.entries(perDiff)
      .map(([difficulty, stats]) => ({
        difficulty,
        difficultyLabel: labelMap[difficulty] ?? difficulty,
        correct_mc_count: stats?.correct_mc_count ?? 0,
        incorrect_mc_count: stats?.incorrect_mc_count ?? 0,
      }))
      .sort((a, b) => order.indexOf(a.difficulty) - order.indexOf(b.difficulty))
      .map(({ difficultyLabel, correct_mc_count, incorrect_mc_count }) => ({
        difficulty: difficultyLabel,
        correct_mc_count,
        incorrect_mc_count,
      }));
  }, [malwareSummary]);

  const avgScore = useMemo(() => {
    const raw = malwareSummary?.stat_per_model?.avg_score ?? 0;
    return Number(raw).toFixed(2);
  }, [malwareSummary]);

  return (
    <Box sx={{ bgcolor: "#f4f4f4", width: "100%" }}>
      <Grid container spacing={1} alignItems="stretch">
        {/* Left Section (Description + Info Cards) */}
        <Grid
          item
          xs={12}
          md={12}
          lg={12}
          xl={8}
          container
          spacing={1}
          direction="column"
          sx={{ display: "flex" }}
        >
          <Grid container spacing={1} sx={{ flex: 1 }}>
            <Grid item xs={12} md={12} lg={8}>
              <Card sx={{ height: "100%", display: "flex", justifyContent: "center", marginLeft: "8px" }}>
                <Typography variant="body2" color="textSecondary" style={{ paddingLeft: "10px" }}>
                  <b>Malware Reasoning</b> Assesses the precision and recall of LLMs in identifying malicious activities from potential malware, such as detecting ransomware or remote access trojans via multiple choice questions where multiple options may be correct.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={12} lg={4}>
              <InfoCard
                title="Total score"
                value={Number(malwareSummary?.stat_per_model?.total_score ?? 0).toFixed(2)}
              />
            </Grid>
          </Grid>

          {/* Info Cards */}
          <Grid item container spacing={1} sx={{ flex: 1 }}>
            <Grid item xs={12} md={12} lg={4}>
              <InfoCard
                title="MCQs answered correctly"
                value={malwareSummary?.stat_per_model?.correct_mc_count ?? 0}
              />
            </Grid>
            <Grid item xs={12} md={12} lg={4}>
              <InfoCard
                title="MCQs answered incorrectly"
                value={malwareSummary?.stat_per_model?.incorrect_mc_count ?? 0}
              />
            </Grid>

            <Grid item xs={12} md={12} lg={4}>
              <InfoCard title="Avg score" value={avgScore} />
            </Grid>
          </Grid>
        </Grid>

        {/* ✅ Right Section (Difficulty Bar Chart instead of Pie) */}
        <Grid
          item
          xs={12}
          md={12}
          lg={12}
          xl={4}
          container
          spacing={2}
          justifyContent="center"
          sx={{
            paddingTop: "8px !important",
            "@media (max-width:1196px)": {
              marginTop: "-8px",
            },
          }}
        >
          <DifficultyBarChart
            data={difficultyData}
            title="Difficulty Wise Distribution"
            padding={16}
          />
        </Grid>
      </Grid>

      {/* Topic Bar Chart (unchanged) */}
      <Card sx={{ width: "100%", marginTop: "9px", paddingTop: "10px", paddingBottom: "10px" }}>
        <Typography variant="h6" pt={0} sx={{ mt: 0, textAlign: "center", fontWeight: "bold" }}>
          Malware Reasoning - Topic Wise Distribution
        </Typography>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={topicData} margin={{ left: 20, right: 20 }} barGap={5}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="topic" stroke="#555" style={{ fontSize: "12px" }} />
            <YAxis stroke="#555" />
            <Tooltip />
            <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: "13px", paddingTop: "10px" }} />
            {series.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                stackId={STACK_ID}
                fill={s.fill}
                barSize={20}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </Box>
  );
};

export default MalwareAnalysisModal;
